import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getLoungeTokenInfo,
  clearLoungeToken,
  isLoungeTokenValid,
} from '../utils/loungeToken';

interface UseLoungeTokenExpirationOptions {
  /** Redirect path when token expires. Defaults to '/lounge-access' */
  redirectPath?: string;
  /** Callback to run when token expires (before redirect) */
  onExpire?: () => void;
}

/**
 * Hook to monitor lounge access token expiration
 * Automatically redirects to lounge access page when token expires
 */
export const useLoungeTokenExpiration = (
  options: UseLoungeTokenExpirationOptions = {},
) => {
  const { redirectPath = '/lounge-access', onExpire } = options;
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleExpiration = useCallback(() => {
    clearLoungeToken();
    onExpire?.();
    navigate(redirectPath, { replace: true });
  }, [navigate, redirectPath, onExpire]);

  const setupExpirationTimer = useCallback(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const tokenInfo = getLoungeTokenInfo();

    if (!tokenInfo) {
      // No valid token, redirect immediately
      handleExpiration();
      return;
    }

    // Set timer for exact expiration time
    timerRef.current = setTimeout(() => {
      handleExpiration();
    }, tokenInfo.remainingMs);
  }, [handleExpiration]);

  useEffect(() => {
    // Initial check and timer setup
    if (!isLoungeTokenValid()) {
      handleExpiration();
      return;
    }

    setupExpirationTimer();

    // Cleanup timer on unmount
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [setupExpirationTimer, handleExpiration]);

  // Return utility functions for manual checks
  return {
    isValid: isLoungeTokenValid,
    clearToken: clearLoungeToken,
    getRemainingTime: () => getLoungeTokenInfo()?.remainingMs ?? 0,
  };
};

export default useLoungeTokenExpiration;
